import { ScoreUtils, useScoreStore } from '@assets/model/ScoreUtils';
import { useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import React, { PureComponent, useRef, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
    Cell
} from 'recharts';

import * as style from './ChartStyles.module.less';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { ChartUtils } from './ChartShared';
import { useNavUtils } from '@assets/utils/NavUtils';
import useMeasure from 'react-use-measure';
import classNames from 'classnames';

import { duration } from 'moment';

interface IChartCompareCompanies {
    companies: string[];
    reportId: string;
}

// Helper function to create white logo version
const createWhiteLogo = (logoURL: string): Promise<string> => {
    return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;

            // Draw original image
            ctx.drawImage(img, 0, 0);

            // Get image data
            const imageData = ctx.getImageData(
                0,
                0,
                canvas.width,
                canvas.height
            );
            const data = imageData.data;

            // First pass: analyze if logo has meaningful dark content to convert
            let visiblePixels = 0;
            let darkPixels = 0;
            let veryLightPixels = 0;

            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const alpha = data[i + 3];

                if (alpha > 50) {
                    // Only count visible pixels
                    visiblePixels++;
                    const gray = 0.299 * r + 0.587 * g + 0.114 * b;

                    if (gray < 128) {
                        // Dark pixels that would be converted to white
                        darkPixels++;
                    } else if (gray > 220) {
                        // Very light pixels (almost white)
                        veryLightPixels++;
                    }
                }
            }

            // If logo has very few dark pixels to convert AND is mostly very light, don't convert
            const darkRatio =
                visiblePixels > 0 ? darkPixels / visiblePixels : 0;
            const veryLightRatio =
                visiblePixels > 0 ? veryLightPixels / visiblePixels : 0;

            // Only skip conversion if there's very little dark content (<10%) AND mostly very light (>80%)
            if (darkRatio < 0.1 && veryLightRatio > 0.8) {
                resolve(logoURL); // Return original logo
                return;
            }

            // Convert to white version
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const alpha = data[i + 3];

                // Convert to grayscale
                const gray = 0.299 * r + 0.587 * g + 0.114 * b;

                // If pixel is not transparent, make it white or transparent based on darkness
                if (alpha > 0) {
                    if (gray < 128) {
                        // Dark pixels become white
                        data[i] = 255; // R
                        data[i + 1] = 255; // G
                        data[i + 2] = 255; // B
                        data[i + 3] = alpha; // Keep original alpha
                    } else {
                        // Light pixels become transparent
                        data[i + 3] = 0;
                    }
                }
            }

            // Put modified data back
            ctx.putImageData(imageData, 0, 0);

            // Convert to data URL
            resolve(canvas.toDataURL());
        };
        img.onerror = () => resolve(logoURL); // Fallback to original
        img.src = logoURL;
    });
};

export const ChartCompareCompanies = observer((p: IChartCompareCompanies) => {
    const store = useStore();
    const nav = useNavUtils();
    const [ref, bounds] = useMeasure();
    const [clickedEntry, setClickedEntry] = React.useState({ id: '' });
    const [whiteLogos, setWhiteLogos] = React.useState<{
        [key: string]: string;
    }>({});

    const scoreStore = useScoreStore();
    var groups = scoreStore.SCORE_FIELDS_GROUPS;
    var data = [];

    // Process logos to white versions
    React.useEffect(() => {
        const processLogos = async () => {
            const logoPromises: { [key: string]: Promise<string> } = {};

            companies.forEach(reportId => {
                const q = store.createDataAccess(reportId);
                if (q) {
                    const logoURL = q.value('logoURL');
                    if (logoURL && !whiteLogos[reportId]) {
                        logoPromises[reportId] = createWhiteLogo(logoURL);
                    }
                }
            });

            const logoResults = await Promise.all(
                Object.entries(logoPromises).map(
                    async ([reportId, promise]) => ({
                        reportId,
                        whiteLogo: await promise
                    })
                )
            );

            const newWhiteLogos = { ...whiteLogos };
            logoResults.forEach(({ reportId, whiteLogo }) => {
                newWhiteLogos[reportId] = whiteLogo;
            });

            setWhiteLogos(newWhiteLogos);
        };

        processLogos();
    }, [p.companies, p.reportId]);

    var companies = [p.reportId, ...p.companies];
    companies.forEach(reportId => {
        var companyEntry = {};
        var q = store.createDataAccess(reportId);
        if (q == null) return;

        companyEntry['id'] = reportId;
        companyEntry['name'] = q.value('company_name');
        companyEntry['Score'] = q.score();

        companyEntry['logo'] = q.value('logoURL');

        if (companyEntry['Score'] == -1) {
            return;
        }
        data.push(companyEntry);
    });

    const renderCustomAxisTick = ({ x, y, payload, visibleTicksCount }) => {
        const originalLogoURL = data[payload.index].logo;
        const reportId = data[payload.index].id;
        const logoURL = whiteLogos[reportId] || originalLogoURL;
        const barWidth = Math.floor((bounds.width / (data.length + 1)) * 0.6); // Approximate way if chart width is known

        // Smart text handling for long names
        const maxCharsPerLine = Math.max(8, Math.floor(barWidth / 8)); // Adjust based on bar width
        const companyName = payload.value;
        const words = companyName.split(' ');

        // If name is too long, try to break into multiple lines
        let displayLines = [];
        if (companyName.length > maxCharsPerLine && words.length > 1) {
            // Try to split into 2 lines
            let line1 = '';
            let line2 = '';
            let currentLine = 1;

            for (const word of words) {
                if (
                    currentLine === 1 &&
                    (line1 + word).length <= maxCharsPerLine
                ) {
                    line1 += (line1 ? ' ' : '') + word;
                } else {
                    currentLine = 2;
                    line2 += (line2 ? ' ' : '') + word;
                }
            }

            if (line1) displayLines.push(line1);
            if (line2) displayLines.push(line2);
        } else if (companyName.length > maxCharsPerLine) {
            // Single word too long - truncate with ellipsis
            displayLines.push(
                companyName.substring(0, maxCharsPerLine - 1) + '…'
            );
        } else {
            // Name fits on one line
            displayLines.push(companyName);
        }

        const imageWidth = barWidth;
        const imageHeight = barWidth * 0.75;

        var img: any = (
            <image
                opacity={1}
                href={logoURL} // Replace with your image URL
                width={barWidth} // Adjust width
                height={barWidth * 0.75} // Adjust height
                preserveAspectRatio="xMidYMax meet"
            />
        );

        return (
            <g transform={`translate(${x},${y})`}>
                {
                    <svg
                        x={-barWidth / 2}
                        y={-30 - imageHeight}
                        width={imageWidth}
                        height={imageHeight}
                    >
                        {img}
                    </svg>
                }

                {displayLines.map((line, lineIndex) => (
                    <text
                        key={lineIndex}
                        fontSize="14"
                        fontFamily="Hanken Grotesk"
                        fontWeight="700"
                        x={0}
                        y={25 + lineIndex * 16} // 16px line height
                        textAnchor="middle"
                        fill="#323B48"
                    >
                        {line}
                    </text>
                ))}
            </g>
        );
    };

    return (
        <>
            <Flex direction="column" fillCell={true}>
                <div
                    ref={ref}
                    style={{
                        height: '100%',
                        width: '100%',
                        position: 'absolute',
                        opacity: 0
                    }}
                ></div>

                <div className={classNames(style.title, style.titleUnderline)}>
                    Benchmarking
                </div>
                <ResponsiveContainer
                    id="benchmarking-chart"
                    width="100%"
                    height="100%"
                >
                    <BarChart
                        width={500}
                        height={300}
                        data={data}
                        margin={{
                            top: 40,
                            left: 20,
                            right: 20,
                            bottom: 20
                        }}
                    >
                        <CartesianGrid
                            vertical={false}
                            horizontal={true}
                            strokeDasharray="3 3"
                        />
                        <YAxis
                            tick={{
                                fontSize: 16,
                                fontFamily: 'Hanken Grotesk',
                                fontWeight: 700,
                                fill: '#323B48'
                            }}
                            tickFormatter={value => `${value}%`}
                        />
                        <Tooltip />

                        <Bar
                            dataKey={'Score'}
                            stackId="a"
                            radius={[4, 4, 0, 0]}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        entry.id === p.reportId
                                            ? '#133971'
                                            : '#A29F93'
                                    }
                                    stroke={
                                        clickedEntry.id === entry.id
                                            ? '#212529'
                                            : 'none'
                                    }
                                    strokeWidth={2}
                                    onClick={() => {
                                        if (entry.id == p.reportId) return;
                                        setClickedEntry({ id: entry.id });
                                        setTimeout(() => {
                                            setClickedEntry({ id: '' });
                                            nav.toReport(entry.id);
                                        }, 5);
                                    }}
                                />
                            ))}
                            <LabelList
                                dataKey="Score"
                                position="insideTop"
                                formatter={v => v + '%'}
                                offset={10}
                                fill="white"
                                stroke="transparent"
                                style={{
                                    fontSize: '14px',
                                    fontFamily: 'Hanken Grotesk',
                                    fontWeight: 400
                                }}
                            />
                        </Bar>
                        <XAxis
                            dataKey="name"
                            tick={renderCustomAxisTick}
                            interval={0}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </Flex>
        </>
    );
});
